/**
 * API Service
 * Centralized client for communicating with the Anivault backend server.
 * Handles authentication, request formatting, and error handling.
 */

import axios from 'axios';

// In development (localhost:3000), connect directly to local server on :5000.
// In production, use /api which Cloudflare Pages proxies to Railway.
// This eliminates CORS entirely — the browser only talks to the same domain.
const API_BASE = window.location.port === '3000'
    ? 'http://localhost:5000/api'
    : '/api';

// API key for server authentication (set in Cloudflare Pages env vars)
const API_KEY = process.env.REACT_APP_API_KEY || '';

// Inject API key into all backend requests.
// Since API_BASE is /api in production and http://localhost:5000/api in dev,
// all backend URLs always start with /api or http://localhost — never https://graphql.anilist.co.
// This simple check is bulletproof with the Cloudflare proxy architecture.
axios.interceptors.request.use((config) => {
    if (API_KEY) {
        const url = config.url || '';
        const baseURL = config.baseURL || '';
        const isBackend = url.startsWith('/api') || url.startsWith('http://localhost')
            || baseURL.startsWith('/api') || baseURL.startsWith('http://localhost');
        if (isBackend) {
            config.headers['X-API-Key'] = API_KEY;
        }
    }
    return config;
});

/**
 * Get the stored access token
 */
function getToken() {
    // Use centralized token storage with obfuscation
    const { getToken: getStoredToken } = require('./tokenStorage');
    return getStoredToken();
}

/**
 * Create axios instance with auth headers
 */
function createClient() {
    const token = getToken();
    const headers = {};
    if (API_KEY) {
        headers['X-API-Key'] = API_KEY;
    }
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return axios.create({
        baseURL: API_BASE,
        headers,
        timeout: 30000
    });
}

// ============================================================
// AUTH ENDPOINTS
// ============================================================

/**
 * Login to the backend server with AniList credentials
 * Called after successful AniList OAuth
 */
export async function serverLogin(accessToken, username, anilistId, avatar) {
    try {
        const response = await axios.post(`${API_BASE}/auth/login`, {
            accessToken,
            username,
            anilistId,
            avatar
        });
        return response.data;
    } catch (error) {
        console.error('Server login failed:', error);
        return null;
    }
}

/**
 * Get current user info from server
 */
export async function getServerUser() {
    try {
        const client = createClient();
        const response = await client.get('/auth/me');
        return response.data?.user || null;
    } catch (error) {
        // 401 is expected if not logged in
        if (error.response?.status !== 401) {
            console.error('Get server user failed:', error);
        }
        return null;
    }
}

// ============================================================
// ANIME CATALOG ENDPOINTS
// ============================================================

/**
 * Search anime catalog on the server
 * @param {string} query - Search query string
 * @param {Object} filters - Optional filters (format, status, season, year, type)
 * @param {number} limit - Max results to return
 * @param {AbortSignal} [signal] - Optional AbortSignal to cancel the request
 */
export async function searchAnime(query, filters = {}, limit = 50, signal) {
    try {
        const params = new URLSearchParams();
        if (query) params.set('q', query);
        if (filters.format) params.set('format', filters.format);
        if (filters.status) params.set('status', filters.status);
        if (filters.season) params.set('season', filters.season);
        if (filters.year) params.set('year', filters.year);
        if (filters.type) params.set('type', filters.type);
        params.set('limit', limit.toString());

        const response = await axios.get(`${API_BASE}/anime/search?${params.toString()}`, { signal });
        return response.data?.results || [];
    } catch (error) {
        // Don't log cancelled requests — they are expected during rapid typing
        if (axios.isCancel(error) || error.name === 'CanceledError') {
            return [];
        }
        console.error('Search anime failed:', error);
        return [];
    }
}

/**
 * Get anime by ID from server
 */
export async function getAnimeById(animeId) {
    try {
        const response = await axios.get(`${API_BASE}/anime/${animeId}`);
        return response.data;
    } catch (error) {
        console.error('Get anime failed:', error);
        return null;
    }
}

/**
 * Get multiple anime by IDs
 */
export async function getAnimeByIds(ids) {
    try {
        if (!ids || ids.length === 0) return [];
        const response = await axios.post(`${API_BASE}/anime/batch`, { ids });
        return response.data?.results || [];
    } catch (error) {
        console.error('Batch get anime failed:', error);
        return [];
    }
}

/**
 * Get related anime for a given anime ID
 */
export async function getRelatedAnime(animeId) {
    try {
        const response = await axios.get(`${API_BASE}/anime/${animeId}/relations`);
        return response.data?.relations || [];
    } catch (error) {
        console.error('Get relations failed:', error);
        return [];
    }
}

/**
 * Get catalog statistics
 */
export async function getCatalogStats() {
    try {
        const response = await axios.get(`${API_BASE}/anime/stats`);
        return response.data;
    } catch (error) {
        console.error('Get stats failed:', error);
        return null;
    }
}

/**
 * Get relations for multiple anime at once (batch)
 * Returns: { [animeId]: { relations: { edges: [...] } } }
 */
export async function getBatchRelations(ids) {
    try {
        if (!ids || ids.length === 0) return {};
        const response = await axios.post(`${API_BASE}/anime/batch-relations`, { ids });
        return response.data?.results || {};
    } catch (error) {
        console.error('Batch relations failed:', error);
        return {};
    }
}

// ============================================================
// USER DATA ENDPOINTS (require auth)
// ============================================================

/**
 * Get user's removed anime IDs
 */
export async function getRemovedAnimeFromServer() {
    try {
        const client = createClient();
        const response = await client.get('/user/removed-anime');
        return response.data?.removedAnimeIds || [];
    } catch (error) {
        console.error('Get removed anime failed:', error);
        return [];
    }
}

/**
 * Add anime to removed list on server
 */
export async function addRemovedAnimeOnServer(animeId) {
    try {
        const client = createClient();
        await client.post(`/user/removed-anime/${animeId}`);
        return true;
    } catch (error) {
        console.error('Add removed anime failed:', error);
        return false;
    }
}

/**
 * Restore anime from removed list on server
 */
export async function removeRemovedAnimeOnServer(animeId) {
    try {
        const client = createClient();
        await client.delete(`/user/removed-anime/${animeId}`);
        return true;
    } catch (error) {
        console.error('Remove from removed list failed:', error);
        return false;
    }
}

/**
 * Batch add anime to removed list on server
 */
export async function batchAddRemovedAnimeOnServer(animeIds) {
    try {
        const client = createClient();
        await client.post('/user/removed-anime-batch', { animeIds });
        return true;
    } catch (error) {
        console.error('Batch add removed anime failed:', error);
        return false;
    }
}

/**
 * Get user's dropped-related anime IDs from server
 */
export async function getDroppedRelatedFromServer() {
    try {
        const client = createClient();
        const response = await client.get('/user/dropped-related');
        return response.data?.droppedRelatedIds || [];
    } catch (error) {
        console.error('Get dropped-related anime failed:', error);
        return [];
    }
}

/**
 * Batch add anime to dropped-related list on server
 */
export async function batchAddDroppedRelatedOnServer(animeIds) {
    try {
        const client = createClient();
        await client.post('/user/dropped-related/batch', { animeIds });
        return true;
    } catch (error) {
        console.error('Batch add dropped-related anime failed:', error);
        return false;
    }
}

/**
 * Get user's watch links from server (requires OAuth)
 */
export async function getWatchLinksFromServer() {
    try {
        const token = getToken();
        if (!token) return null;

        const client = createClient();
        const response = await client.get('/user/watch-links');
        return response.data?.watchLinks || null;
    } catch (error) {
        console.error('Get watch links failed:', error);
        return null;
    }
}

/**
 * Save user's watch links to server (requires OAuth)
 */
export async function saveWatchLinksToServer(links) {
    try {
        const token = getToken();
        if (!token) return false;

        const client = createClient();
        await client.put('/user/watch-links', { links });
        return true;
    } catch (error) {
        console.error('Save watch links failed:', error);
        return false;
    }
}

/**
 * Get a user setting from server
 */
export async function getSettingFromServer(key) {
    try {
        const client = createClient();
        const response = await client.get(`/user/settings/${key}`);
        return response.data?.value ?? null;
    } catch (error) {
        console.error(`Get setting '${key}' failed:`, error);
        return null;
    }
}

/**
 * Save a user setting to server
 */
export async function saveSettingToServer(key, value) {
    try {
        const client = createClient();
        await client.put(`/user/settings/${key}`, { value });
        return true;
    } catch (error) {
        console.error(`Save setting '${key}' failed:`, error);
        return false;
    }
}

/**
 * Get all user settings from server
 */
export async function getAllSettingsFromServer() {
    try {
        const client = createClient();
        const response = await client.get('/user/settings');
        return response.data?.settings || {};
    } catch (error) {
        console.error('Get all settings failed:', error);
        return {};
    }
}



/**
 * Build anime groups on the server (single endpoint replaces batch-relations + batch-get + local build)
 * No auth required — sends only user list fields (mediaId, status, progress, score).
 * The server enriches media data from its own catalog.
 */
export async function buildGroupsOnServer(entries, titlePreference = 'english') {
    try {
        // Strip to minimal fields — server has full catalog data, only needs user list info
        const minimalEntries = entries.map(entry => ({
            mediaId: entry.media?.id || entry.mediaId,
            status: entry.status,
            score: entry.score || 0,
            progress: entry.progress || 0,
            repeat: entry.repeat || 0,
            startedAt: entry.startedAt || null,
            completedAt: entry.completedAt || null,
        }));

        const response = await axios.post(`${API_BASE}/user/build-groups`, {
            entries: minimalEntries,
            titlePreference
        }, { timeout: 60000 }); // 60s timeout for large lists
        return response.data;
    } catch (error) {
        console.error('Server-side group build failed:', error.message);
        return null;
    }
}

/**
 * Get pre-computed anime clusters for user's anime IDs
 * Uses the hybrid approach — server returns pre-computed franchise clusters
 * with catalog data and relations for all media in matching clusters
 */
export async function getClustersForAnime(animeIds) {
    try {
        const response = await axios.post(`${API_BASE}/anime/clusters`, {
            ids: animeIds,
            mediaType: 'ANIME'
        }, { timeout: 60000 });
        return response.data;
    } catch (error) {
        console.error('Failed to get anime clusters:', error.message);
        return null;
    }
}

/**
 * Get pre-computed manga clusters for user's manga IDs
 * Uses the hybrid approach — server returns pre-computed franchise clusters
 * with catalog data and relations for all media in matching clusters
 */
export async function getClustersForManga(mangaIds) {
    try {
        const response = await axios.post(`${API_BASE}/anime/clusters`, {
            ids: mangaIds,
            mediaType: 'MANGA'
        }, { timeout: 30000 });
        return response.data;
    } catch (error) {
        console.error('Failed to get manga clusters:', error.message);
        return null;
    }
}

/**
 * Build manga groups server-side
 * Unlike anime, sends full entries + embedded relations (no server manga catalog)
 */
export async function buildMangaGroupsOnServer(entries, relationsData, titlePreference = 'english') {
    try {
        const response = await axios.post(`${API_BASE}/user/build-manga-groups`, {
            entries,
            relationsData,
            titlePreference
        }, { timeout: 60000 });
        return response.data;
    } catch (error) {
        console.error('Server-side manga group build failed:', error.message);
        return null;
    }
}

/**
 * Get all user data from the server (public endpoint)
 * Returns anime list, manga list, removed, groups cache, settings, etc.
 * @param {string} username - The username to fetch data for
 */
export async function getAllUserData(username) {
    try {
        if (!username) {
            console.warn('getAllUserData called without username, skipping');
            return null;
        }
        const response = await axios.get(`${API_BASE}/user/all-data`, {
            params: { username },
            timeout: 15000
        });
        return response.data;
    } catch (error) {
        console.warn('Get all user data failed:', error.message);
        return null;
    }
}

/**
 * Check if server is available
 */
export async function checkServerHealth() {
    try {
        const response = await axios.get(`${API_BASE}/health`, { timeout: 5000 });
        return response.data;
    } catch (error) {
        return null;
    }
}

// ============================================================
// MANGA USER DATA ENDPOINTS (require auth)
// ============================================================

/**
 * Get user's removed manga IDs from server
 */
export async function getRemovedMangaFromServer() {
    try {
        const client = createClient();
        const response = await client.get('/user/removed-manga');
        return response.data?.removedMangaIds || [];
    } catch (error) {
        console.error('Get removed manga failed:', error);
        return [];
    }
}

/**
 * Add manga to removed list on server
 */
export async function addRemovedMangaOnServer(mangaId) {
    try {
        const client = createClient();
        await client.post('/user/removed-manga', { mangaId });
        return true;
    } catch (error) {
        console.error('Add removed manga failed:', error);
        return false;
    }
}

/**
 * Restore manga from removed list on server
 */
export async function removeRemovedMangaOnServer(mangaId) {
    try {
        const client = createClient();
        await client.delete(`/user/removed-manga/${mangaId}`);
        return true;
    } catch (error) {
        console.error('Remove from removed manga list failed:', error);
        return false;
    }
}

/**
 * Batch add manga to removed list on server
 */
export async function batchAddRemovedMangaOnServer(mangaIds) {
    try {
        const client = createClient();
        await client.post('/user/removed-manga/batch', { mangaIds });
        return true;
    } catch (error) {
        console.error('Batch add removed manga failed:', error);
        return false;
    }
}

/**
 * Clear all removed manga on server
 */
export async function clearAllRemovedMangaOnServer() {
    try {
        const client = createClient();
        await client.delete('/user/removed-manga');
        return true;
    } catch (error) {
        console.error('Clear removed manga failed:', error);
        return false;
    }
}



/**
 * Get user's dropped-related manga IDs from server
 */
export async function getDroppedRelatedMangaFromServer() {
    try {
        const client = createClient();
        const response = await client.get('/user/dropped-related-manga');
        return response.data?.droppedRelatedMangaIds || [];
    } catch (error) {
        console.error('Get dropped-related manga failed:', error);
        return [];
    }
}

/**
 * Batch add manga to dropped-related list on server
 */
export async function batchAddDroppedRelatedMangaOnServer(mangaIds) {
    try {
        const client = createClient();
        await client.post('/user/dropped-related-manga/batch', { mangaIds });
        return true;
    } catch (error) {
        console.error('Batch add dropped-related manga failed:', error);
        return false;
    }
}

// ============================================================
// COMPARE ENDPOINTS
// ============================================================

/**
 * Run a comparison between two users on the server
 */
export async function runComparison(userAUsername, userAEntries, userBUsername, userBEntries, userBAvatar, titlePreference) {
    const response = await axios.post(`${API_BASE}/compare/run-comparison`, {
        userAUsername, userAEntries, userBUsername, userBEntries, userBAvatar, titlePreference
    }, { timeout: 60000 });
    return response.data;
}

// ============================================================
// COMPARISON STORAGE (localStorage — saves ~800KB/user on server)
// ============================================================

const COMPARISON_STORAGE_KEY = 'anivault_comparisons';
const MANGA_COMPARISON_STORAGE_KEY = 'anivault_manga_comparisons';

/**
 * Get all stored comparisons from localStorage
 */
function _getStoredComparisons(username, storageKey) {
    try {
        const raw = localStorage.getItem(`${storageKey}_${username}`);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

/**
 * Save comparisons array to localStorage
 */
function _setStoredComparisons(username, comparisons, storageKey) {
    try {
        localStorage.setItem(`${storageKey}_${username}`, JSON.stringify(comparisons));
    } catch (e) {
        console.warn('Failed to save comparison to localStorage:', e.message);
    }
}

/**
 * Get all saved comparisons for a user (from localStorage)
 */
export async function getSavedComparisons(username) {
    const comparisons = _getStoredComparisons(username, COMPARISON_STORAGE_KEY);
    // Return summary view (without full comparison_data for the list)
    const savedComparisons = comparisons.map(c => ({
        userBUsername: c.userBUsername,
        userBAvatar: c.userBAvatar,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        summary: {
            compatibilityScore: c.metrics?.compatibilityScore,
            commonAnimeCount: c.metrics?.overview?.commonAnimeCount,
        }
    }));
    return { comparisons: savedComparisons, count: savedComparisons.length };
}

/**
 * Get a specific saved comparison's full data (from localStorage)
 */
export async function getSavedComparison(userAUsername, userBUsername) {
    const comparisons = _getStoredComparisons(userAUsername, COMPARISON_STORAGE_KEY);
    const found = comparisons.find(c => c.userBUsername === userBUsername);
    if (!found) {
        throw new Error('Comparison not found');
    }
    return { metrics: found.metrics, stats: found.stats };
}

/**
 * Refresh a saved comparison — re-runs on server, saves locally
 */
export async function refreshComparison(userAUsername, userBUsername, userAEntries, userBEntries, userBAvatar, titlePreference) {
    // Run fresh comparison on server
    const result = await runComparison(userAUsername, userAEntries, userBUsername, userBEntries, userBAvatar, titlePreference);

    // Update in localStorage
    const comparisons = _getStoredComparisons(userAUsername, COMPARISON_STORAGE_KEY);
    const idx = comparisons.findIndex(c => c.userBUsername === userBUsername);
    const entry = {
        userBUsername,
        userBAvatar: userBAvatar || null,
        metrics: result.metrics,
        stats: result.stats,
        createdAt: idx >= 0 ? comparisons[idx].createdAt : Date.now(),
        updatedAt: Date.now()
    };
    if (idx >= 0) {
        comparisons[idx] = entry;
    } else {
        comparisons.push(entry);
    }
    _setStoredComparisons(userAUsername, comparisons, COMPARISON_STORAGE_KEY);

    return result;
}

/**
 * Delete a saved comparison (from localStorage)
 */
export async function deleteSavedComparison(userAUsername, userBUsername) {
    const comparisons = _getStoredComparisons(userAUsername, COMPARISON_STORAGE_KEY);
    const filtered = comparisons.filter(c => c.userBUsername !== userBUsername);
    _setStoredComparisons(userAUsername, filtered, COMPARISON_STORAGE_KEY);
    return { success: true };
}

/**
 * Check if a user exists on the server
 */
export async function checkUserExists(username) {
    try {
        const response = await axios.get(`${API_BASE}/user/check-user`, {
            params: { username }, timeout: 5000
        });
        return response.data;
    } catch (error) {
        return { exists: false };
    }
}

// ============================================================
// MANGA COMPARE ENDPOINTS
// ============================================================

/**
 * Run a manga comparison between two users on the server
 */
export async function runMangaComparison(userAUsername, userAEntries, userBUsername, userBEntries, userBAvatar, titlePreference) {
    const response = await axios.post(`${API_BASE}/compare/run-manga-comparison`, {
        userAUsername, userAEntries, userBUsername, userBEntries, userBAvatar, titlePreference
    }, { timeout: 60000 });
    return response.data;
}

/**
 * Save a manga comparison result (to localStorage)
 */
export async function saveMangaComparison(userAUsername, userBUsername, userBAvatar, comparisonData) {
    const comparisons = _getStoredComparisons(userAUsername, MANGA_COMPARISON_STORAGE_KEY);
    const idx = comparisons.findIndex(c => c.userBUsername === userBUsername);
    const entry = {
        userBUsername,
        userBAvatar: userBAvatar || null,
        metrics: comparisonData,
        createdAt: idx >= 0 ? comparisons[idx].createdAt : Date.now(),
        updatedAt: Date.now()
    };
    if (idx >= 0) {
        comparisons[idx] = entry;
    } else {
        comparisons.push(entry);
    }
    _setStoredComparisons(userAUsername, comparisons, MANGA_COMPARISON_STORAGE_KEY);
    return { success: true };
}

/**
 * Get all saved manga comparisons for a user (from localStorage)
 */
export async function getSavedMangaComparisons(username) {
    const comparisons = _getStoredComparisons(username, MANGA_COMPARISON_STORAGE_KEY);
    const savedComparisons = comparisons.map(c => ({
        userBUsername: c.userBUsername,
        userBAvatar: c.userBAvatar,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        summary: {
            compatibilityScore: c.metrics?.compatibilityScore,
            commonMangaCount: c.metrics?.overview?.commonMangaCount,
        }
    }));
    return { comparisons: savedComparisons, count: savedComparisons.length };
}

/**
 * Get a specific saved manga comparison (from localStorage)
 */
export async function getSavedMangaComparison(userAUsername, userBUsername) {
    const comparisons = _getStoredComparisons(userAUsername, MANGA_COMPARISON_STORAGE_KEY);
    const found = comparisons.find(c => c.userBUsername === userBUsername);
    if (!found) {
        throw new Error('Manga comparison not found');
    }
    return { metrics: found.metrics };
}

/**
 * Delete a saved manga comparison (from localStorage)
 */
export async function deleteSavedMangaComparison(userAUsername, userBUsername) {
    const comparisons = _getStoredComparisons(userAUsername, MANGA_COMPARISON_STORAGE_KEY);
    const filtered = comparisons.filter(c => c.userBUsername !== userBUsername);
    _setStoredComparisons(userAUsername, filtered, MANGA_COMPARISON_STORAGE_KEY);
    return { success: true };
}

// ============================================================
// SUGGESTIONS ENDPOINTS
// ============================================================

/**
 * Submit a new suggestion (no auth required — sends username in body)
 * @param {{ username: string, category: string, title: string, description: string }} data
 */
export async function submitSuggestion(data) {
    try {
        const response = await axios.post(`${API_BASE}/suggestions`, data);
        return response.data;
    } catch (error) {
        console.error('Submit suggestion failed:', error);
        throw error;
    }
}

/**
 * Get all suggestions (admin view)
 * @param {{ status?: string, category?: string }} filters
 */
export async function getAllSuggestions(filters = {}) {
    try {
        const params = new URLSearchParams();
        if (filters.status) params.set('status', filters.status);
        if (filters.category) params.set('category', filters.category);
        const response = await axios.get(`${API_BASE}/suggestions?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Get suggestions failed:', error);
        return { suggestions: [], count: 0 };
    }
}

/**
 * Update a suggestion (admin action)
 * @param {number} id
 * @param {{ status?: string, admin_notes?: string }} data
 */
export async function updateSuggestion(id, data) {
    try {
        const response = await axios.put(`${API_BASE}/suggestions/${id}`, data);
        return response.data;
    } catch (error) {
        console.error('Update suggestion failed:', error);
        throw error;
    }
}

/**
 * Delete a suggestion (admin action)
 */
export async function deleteSuggestion(id) {
    try {
        const response = await axios.delete(`${API_BASE}/suggestions/${id}`);
        return response.data;
    } catch (error) {
        console.error('Delete suggestion failed:', error);
        throw error;
    }
}

/**
 * Get suggestions with admin responses for a user
 * @param {string} username
 */
export async function getUserResponses(username) {
    try {
        const response = await axios.get(`${API_BASE}/suggestions/responses/${encodeURIComponent(username)}`);
        return response.data;
    } catch (error) {
        console.error('Get user responses failed:', error);
        return { suggestions: [], count: 0, unreadCount: 0 };
    }
}

/**
 * User sends a reply on a suggestion thread
 * @param {number} suggestionId
 * @param {string} username
 * @param {string} message
 */
export async function sendReply(suggestionId, username, message) {
    try {
        const response = await axios.post(`${API_BASE}/suggestions/${suggestionId}/reply`, { username, message });
        return response.data;
    } catch (error) {
        console.error('Send reply failed:', error);
        throw error;
    }
}

/**
 * Mark all responses as read for a user (persists across refreshes)
 * @param {string} username
 */
export async function markResponsesAsRead(username) {
    try {
        await axios.post(`${API_BASE}/suggestions/responses/${encodeURIComponent(username)}/mark-read`);
    } catch (error) {
        console.error('Mark as read failed:', error);
    }
}

export default {
    // Auth
    serverLogin,
    getServerUser,
    // Catalog
    searchAnime,
    getAnimeById,
    getAnimeByIds,
    getRelatedAnime,
    getBatchRelations,
    getCatalogStats,
    // User data (anime)
    getRemovedAnimeFromServer,
    addRemovedAnimeOnServer,
    removeRemovedAnimeOnServer,
    batchAddRemovedAnimeOnServer,
    getDroppedRelatedFromServer,
    batchAddDroppedRelatedOnServer,
    getWatchLinksFromServer,
    saveWatchLinksToServer,
    getSettingFromServer,
    saveSettingToServer,
    getAllSettingsFromServer,
    buildGroupsOnServer,
    buildMangaGroupsOnServer,
    getAllUserData,
    checkServerHealth,
    // User data (manga)
    getRemovedMangaFromServer,
    addRemovedMangaOnServer,
    removeRemovedMangaOnServer,
    batchAddRemovedMangaOnServer,
    clearAllRemovedMangaOnServer,
    getDroppedRelatedMangaFromServer,
    batchAddDroppedRelatedMangaOnServer,
    // Compare (anime)
    runComparison,
    getSavedComparisons,
    getSavedComparison,
    refreshComparison,
    deleteSavedComparison,
    checkUserExists,
    // Compare (manga)
    runMangaComparison,
    saveMangaComparison,
    getSavedMangaComparisons,
    getSavedMangaComparison,
    deleteSavedMangaComparison,
    // Suggestions
    submitSuggestion,
    getAllSuggestions,
    updateSuggestion,
    deleteSuggestion,
    getUserResponses,
    sendReply,
    markResponsesAsRead
};
